import { useCallback, useMemo, useState } from 'react';
import { loadTwoFactorRecovery, sendAdminRecoveryEmail, sendRecoveryEmail } from '../data/twoFactorRecovery';
import { deriveTwoFactorErrorType, deriveTwoFactorType } from '../utils/twoFactorRecovery';
export const useTwoFactorRecovery = precedence => {
  // state
  const [ready, setReady] = useState(false);
  const [loadingError, setLoadingError] = useState(false);
  const [token, setToken] = useState();
  const [email, setEmail] = useState('');
  const [admins, setAdmins] = useState([]);
  const [twoFactorRecoveryOpen, setTwoFactorRecoveryOpen] = useState(false);
  const [twoFactorRecoveryType, setTwoFactorRecoveryType] = useState();
  const [isRecovering, setIsRecovering] = useState(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [recoveryAttempted, setRecoveryAttempted] = useState(false);
  const [recoveryNeedsSupport, setRecoveryNeedsSupport] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [isRecoveryEmailInErrorState, setRecoveryEmailInErrorState] = useState(false);

  // UI callbacks
  const openTwoFactorRecovery = useCallback(() => setTwoFactorRecoveryOpen(true), [setTwoFactorRecoveryOpen]);
  const closeTwoFactorRecovery = useCallback(() => setTwoFactorRecoveryOpen(false), [setTwoFactorRecoveryOpen]);
  const goToAdmin = useCallback(() => setTwoFactorRecoveryType('admin'), [setTwoFactorRecoveryType]);
  const goToSupport = useCallback(() => setTwoFactorRecoveryType('support'), [setTwoFactorRecoveryType]);

  // async actions
  const handleError = useCallback(error => {
    const errorType = deriveTwoFactorErrorType(error.responseText);
    if (errorType === 'TOKEN_EXPIRED') setIsTokenExpired(true);
    if (errorType === 'NEEDS_SUPPORT') setRecoveryNeedsSupport(true);
  }, [setIsTokenExpired, setRecoveryNeedsSupport]);
  const setupTwoFactorRecoveryPage = useCallback(() => loadTwoFactorRecovery(precedence).then(data => {
    setToken(data.token);
    setEmail(data.email);
    setAdmins(data.admins);
    setTwoFactorRecoveryType(deriveTwoFactorType(data.eligible, data.shouldForce));
  }).catch(err => {
    console.log(err);
    setLoadingError(true);
    handleError(err);
  }).finally(() => setReady(true)), [precedence, handleError, setLoadingError, setToken, setEmail, setAdmins, setTwoFactorRecoveryType]);
  const makeSendEmail = useCallback(emailAction => () => {
    setIsRecovering(true);
    setRecoveryAttempted(true);
    emailAction(token).then(() => {
      setRecoverySent(true);
    }).catch(err => {
      setRecoveryEmailInErrorState(true);
      handleError(err);
    }).finally(() => setIsRecovering(false));
  }, [setIsRecovering, token, handleError]);
  const [onClickAdminRecovery, onClickSupportRecovery] = useMemo(() => [makeSendEmail(sendAdminRecoveryEmail), makeSendEmail(sendRecoveryEmail)], [makeSendEmail]);
  return useMemo(() => ({
    admins,
    closeTwoFactorRecovery,
    email,
    goToAdmin,
    goToSupport,
    isRecovering,
    isRecoveryEmailInErrorState,
    isTokenExpired,
    loadingError,
    onClickAdminRecovery,
    onClickSupportRecovery,
    openTwoFactorRecovery,
    ready,
    recoveryAttempted,
    recoveryNeedsSupport,
    recoverySent,
    twoFactorRecoveryOpen,
    twoFactorRecoveryType,
    setupTwoFactorRecoveryPage
  }), [admins, closeTwoFactorRecovery, email, goToAdmin, goToSupport, isRecovering, isRecoveryEmailInErrorState, isTokenExpired, loadingError, onClickAdminRecovery, onClickSupportRecovery, openTwoFactorRecovery, ready, recoveryAttempted, recoveryNeedsSupport, recoverySent, twoFactorRecoveryOpen, twoFactorRecoveryType, setupTwoFactorRecoveryPage]);
};